import axiosClient from "./axiosClient"

/**
 * Sube un archivo a S3 usando una Presigned URL generada por el backend.
 *
 * Flujo:
 *   1. Pide al backend una Presigned URL (PUT) para el archivo
 *   2. Sube el archivo directo a S3 con esa URL temporal
 *   3. Retorna la URL pública del archivo subido
 *
 * El backend debe exponer:
 *   POST /storage/presigned-url
 *   Body: { folder: string, filename: string, contentType: string }
 *   Response: { url: string (presigned PUT URL), publicUrl: string }
 *
 * @param {File} file - El archivo a subir
 * @param {string} folder - Carpeta en S3 (ej: "logos")
 * @param {string} [customName] - Nombre personalizado sin extensión
 * @returns {Promise<string>} - URL pública del archivo en S3
 */
export const uploadToS3 = async (file, folder = "logos", customName) => {
    const ext = file.name.split(".").pop()
    const filename = customName
        ? `${customName}.${ext}`
        : `${Date.now()}_${file.name}`

    // 1. Pedir Presigned URL al backend (axiosClient envía el JWT automáticamente)
    const response = await axiosClient.post("storage/presigned-url", {
        folder,
        filename,
        contentType: file.type,
    })

    // El backend responde: { success: true, data: { url, publicUrl, key } }
    const responseData = response.data?.data || response.data

    // El campo puede llamarse 'url' o 'presignedUrl' según el backend
    const presignedUrl = responseData?.url || responseData?.presignedUrl
    const publicUrl = responseData?.publicUrl

    if (!presignedUrl) {
        console.error("[s3Upload] Respuesta del backend:", response.data)
        throw new Error("El backend no devolvió una URL firmada válida. Verifique que el endpoint /storage/presigned-url está activo.")
    }

    // 2. Subir el archivo directo a S3 con la presigned URL
    //    Usar fetch nativo (NO axiosClient) para no agregar el header Authorization
    //    que rompería la firma de la presigned URL
    const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
            "Content-Type": file.type,
        },
    })

    if (!uploadResponse.ok) {
        throw new Error(`Error al subir a S3: ${uploadResponse.status} ${uploadResponse.statusText}`)
    }

    // 3. Retornar la URL pública
    return publicUrl
}

export default uploadToS3
